import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import QuickActionButton from '../../components/ui/QuickActionButton';
import MealCategoryCard from './components/MealCategoryCard';
import DailyNutritionSummary from './components/DailyNutritionSummary';
import QuickAddSection from './components/QuickAddSection';
import WeeklyTrendsChart from './components/WeeklyTrendsChart';
import MealPlanningSection from './components/MealPlanningSection';
import NutritionInsights from './components/NutritionInsights';
import FoodSearchModal from './components/FoodSearchModal';

const NutritionTracker = () => {
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');

  const mealData = {
    breakfast: {
      meals: [
      {
        id: 1,
        name: "Oatmeal with Berries",
        image: "https://images.unsplash.com/photo-1607030298395-ed979957b555",
        imageAlt: "Bowl of oatmeal topped with fresh blueberries and strawberries on wooden table",
        calories: 320,
        serving: "1 bowl"
      },
      {
        id: 2,
        name: "Greek Yogurt",
        image: "https://images.unsplash.com/photo-1670843839025-d50924a51f31",
        imageAlt: "Glass bowl of creamy Greek yogurt with honey drizzle and nuts",
        calories: 150,
        serving: "200g"
      }],

      consumed: 470,
      target: 600
    },
    lunch: {
      meals: [
      {
        id: 3,
        name: "Grilled Chicken Salad",
        image: "https://images.unsplash.com/photo-1582034986517-30d163aa1da9",
        imageAlt: "Fresh salad with grilled chicken strips, mixed greens, tomatoes and cucumbers",
        calories: 420,
        serving: "1 plate"
      }],

      consumed: 420,
      target: 700
    },
    dinner: {
      meals: [],
      consumed: 0,
      target: 650
    },
    snacks: {
      meals: [
      {
        id: 4,
        name: "Protein Shake",
        image: "https://img.rocket.new/generatedImages/rocket_gen_img_128605ca6-1768862638717.png",
        imageAlt: "Chocolate protein shake in clear glass with banana slices on side",
        calories: 180,
        serving: "1 serving"
      }],

      consumed: 180,
      target: 250
    }
  };

  const nutritionSummary = {
    totalCalories: 1070,
    targetCalories: 2200,
    macros: {
      protein: { consumed: 85, target: 165 },
      carbs: { consumed: 120, target: 248 },
      fats: { consumed: 35, target: 73 }
    },
    hydration: { consumed: 1.8, target: 3.0 }
  };

  const recentFoods = [
  {
    id: 1,
    name: "Banana",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ac8d516e-1768121441365.png",
    imageAlt: "Fresh yellow banana on white background with natural lighting",
    calories: 105
  },
  {
    id: 2,
    name: "Almonds",
    image: "https://images.unsplash.com/photo-1606791464171-1fff3dd7829d",
    imageAlt: "Handful of raw almonds in wooden bowl on rustic table",
    calories: 160
  },
  {
    id: 3,
    name: "Apple",
    image: "https://images.unsplash.com/photo-1587092658848-828238691286",
    imageAlt: "Red apple with green leaf on wooden surface with natural light",
    calories: 95
  },
  {
    id: 4,
    name: "Protein Bar",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15a948df8-1767124215921.png",
    imageAlt: "Chocolate protein bar unwrapped showing texture on white background",
    calories: 200
  }];


  const weeklyData = [
  { day: 'Mon', calories: 2100, target: 2200 },
  { day: 'Tue', calories: 2250, target: 2200 },
  { day: 'Wed', calories: 2050, target: 2200 },
  { day: 'Thu', calories: 2180, target: 2200 },
  { day: 'Fri', calories: 2300, target: 2200 },
  { day: 'Sat', calories: 2150, target: 2200 },
  { day: 'Sun', calories: 1070, target: 2200 }];


  const upcomingMeals = [
  {
    id: 1,
    name: "Grilled Salmon with Quinoa",
    image: "https://images.unsplash.com/photo-1539136788836-5699e78bfc75",
    imageAlt: "Grilled salmon fillet with quinoa and roasted vegetables on white plate",
    date: "Tomorrow",
    category: "dinner",
    calories: 520,
    prepTime: 25
  },
  {
    id: 2,
    name: "Chicken Stir Fry",
    image: "https://images.unsplash.com/photo-1730162191706-d2c230c4902e",
    imageAlt: "Colorful chicken stir fry with bell peppers and broccoli in wok",
    date: "Jan 25",
    category: "lunch",
    calories: 450,
    prepTime: 20
  }];


  const insights = [
  {
    id: 1,
    type: 'success',
    title: 'Great Protein Intake!',
    description: 'You\'re meeting your protein goals consistently. This supports muscle recovery and growth.',
    action: null
  },
  {
    id: 2,
    type: 'warning',
    title: 'Low Hydration',
    description: 'You\'re 40% below your daily water goal. Proper hydration improves workout performance.',
    action: 'Set Reminder'
  },
  {
    id: 3,
    type: 'tip',
    title: 'Pre-Workout Nutrition',
    description: 'Consider adding a carb-rich snack 30-60 minutes before your evening workout for better energy.',
    action: 'View Suggestions'
  },
  {
    id: 4,
    type: 'info',
    title: 'Calorie Deficit Trend',
    description: 'You\'ve maintained a healthy 300-calorie deficit for 5 days. Keep it up for steady progress!',
    action: null
  }];


  const handleAddFood = (category) => {
    setSelectedCategory(category);
    setIsSearchModalOpen(true);
  };

  const handleQuickAdd = (food) => {
    console.log('Quick adding food:', food);
  };

  const handleFoodAdded = (food) => {
    console.log('Food added:', food);
  };

  return (
    <>
      <Helmet>
        <title>Nutrition Tracker - FitWellness</title>
        <meta
          name="description"
          content="Track your daily nutrition, monitor calorie intake, and achieve your fitness goals with comprehensive meal logging and analytics." />

      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="mx-auto max-w-[1440px] px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10 pb-24 lg:pb-10">
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
              Nutrition Tracker
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Monitor your daily intake and stay on track with your nutrition goals
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-2 space-y-6 md:space-y-8">
              <DailyNutritionSummary
                totalCalories={nutritionSummary?.totalCalories}
                targetCalories={nutritionSummary?.targetCalories}
                macros={nutritionSummary?.macros}
                hydration={nutritionSummary?.hydration} />


              <QuickAddSection
                recentFoods={recentFoods}
                onQuickAdd={handleQuickAdd}
                onScanBarcode={() => console.log('Scan barcode')}
                onVoiceInput={() => console.log('Voice input')} />


              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {Object.entries(mealData)?.map(([category, data]) =>
                <MealCategoryCard
                  key={category}
                  category={category}
                  meals={data?.meals}
                  targetCalories={data?.target}
                  consumedCalories={data?.consumed}
                  onAddFood={handleAddFood}
                  onViewDetails={(cat) => console.log('View details:', cat)} />

                )}
              </div>

              <WeeklyTrendsChart weeklyData={weeklyData} />
            </div>

            <div className="space-y-6 md:space-y-8">
              <NutritionInsights insights={insights} />
              <MealPlanningSection
                upcomingMeals={upcomingMeals}
                onPlanMeal={() => console.log('Plan meal')}
                onViewRecipes={() => console.log('View recipes')} />

            </div>
          </div>
        </main>

        <MobileBottomNav />
        <QuickActionButton />

        <FoodSearchModal
          isOpen={isSearchModalOpen}
          onClose={() => setIsSearchModalOpen(false)}
          onAddFood={handleFoodAdded}
          category={selectedCategory} />

      </div>
    </>);

};

export default NutritionTracker;